import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Activity, Users, Package, ShoppingCart, FileText,
  ChevronLeft, ChevronRight, Filter, Search
} from 'lucide-react';

const actionLabels = {
  create: 'Buat',
  update: 'Ubah',
  delete: 'Hapus',
  update_status: 'Ubah Status',
  reply: 'Balas',
  login: 'Login',
  export: 'Ekspor',
  import: 'Impor',
};
const resourceLabels = {
  product: 'Produk',
  order: 'Pesanan',
  customer: 'Pelanggan',
  invoice: 'Invoice',
  support_ticket: 'Tiket Support',
  course: 'Kursus',
  membership: 'Membership',
  donation: 'Donasi',
  voucher: 'Voucher',
  setting: 'Pengaturan',
};

const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  update_status: 'bg-amber-100 text-amber-800',
  reply: 'bg-purple-100 text-purple-800',
};

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const limit = 30;

  document.title = 'Log Aktivitas — Tokomoe';

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * limit, limit };
      if (actionFilter) params.action = actionFilter;
      if (resourceFilter) params.resource_type = resourceFilter;
      if (searchEmail) params.user_email = searchEmail;
      const { data } = await api.get('/tenant/activity-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, resourceFilter, searchEmail]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/activity-logs/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const totalPages = Math.ceil(total / limit);
  const fmtDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Log Aktivitas</h1>
        <p className="text-slate-500">Riwayat semua aktivitas di toko Anda</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><Activity className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Total Aktivitas</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100"><Users className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Pengguna Aktif</p>
                <p className="text-xl font-bold">{stats.unique_users}</p>
              </div>
            </CardContent>
          </Card>
          {stats.by_action?.slice(0, 2).map((a, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${i === 0 ? 'bg-amber-100' : 'bg-purple-100'}`}>
                  {i === 0 ? <Package className={`w-5 h-5 ${i === 0 ? 'text-amber-600' : 'text-purple-600'}`} /> : <ShoppingCart className="w-5 h-5 text-purple-600" />}
                </div>
                <div>
                  <p className="text-xs text-slate-500">{actionLabels[a.action] || a.action}</p>
                  <p className="text-xl font-bold">{a.count}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Filter className="w-4 h-4" /> Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}
              className="border rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Semua Aksi</option>
              {Object.entries(actionLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={resourceFilter} onChange={e => { setResourceFilter(e.target.value); setPage(1); }}
              className="border rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Semua Resource</option>
              {Object.entries(resourceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Cari email user..." value={searchEmail}
                onChange={e => { setSearchEmail(e.target.value); setPage(1); }}
                className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Belum ada log aktivitas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Waktu</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">User</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Aksi</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Resource</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logs.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                      <td className="px-4 py-3">{log.user_email || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionColors[log.action] || 'bg-slate-100 text-slate-700'}`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-700">{resourceLabels[log.resource_type] || log.resource_type}</span>
                        {log.resource_id && <span className="text-xs text-slate-400 ml-1">#{log.resource_id.slice(0, 8)}</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate">
                        {log.details && Object.keys(log.details).length > 0
                          ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{total} log aktivitas</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
